import React, { useState } from "react";
import { DateTime } from "luxon";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid2 as Grid,
  Stack,
  Link,
  Card,
  Divider,
  CardActions,
  CardContent,
  IconButton,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download,
  CheckCircle,
  Cancel,
  Person,
  LocationOn,
  AccessTime,
  Description,
  FileDownload,
  PictureAsPdf,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { openBlankTab, setNewTabLocation } from "../../../utils/openNewTab";

function PastNotarizationModal({
  open,
  onClose,
  selectedJob,
  currentUser,
  currentSession,
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingPdfUrls, setLoadingPdfUrls] = useState({});

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const utcDate = DateTime.fromISO(dateString, { zone: "utc" });
    const localDate = utcDate.setZone(currentUser?.timezone);
    return localDate.toFormat("MMMM dd, yyyy hh:mm a");
  };

  console.log("selectedJob: ", selectedJob);

  const isCancelled =
    !!selectedJob?.cancelled_at ||
    (selectedJob?.notarization?.notarization?.appointments?.cancelled?.length ||
      0) > 0;
  const isCompleted =
    !isCancelled &&
    (!!selectedJob?.is_notarized ||
      (selectedJob?.completed_documents?.length || 0) > 0);

  const getStatusInfo = () => {
    if (isCancelled) {
      return {
        status: "Cancelled",
        color: "warning",
        icon: <Cancel />,
      };
    }

    return {
      status: isCompleted ? "Completed Successfully" : "Failed",
      color: isCompleted ? "success" : "error",
      icon: isCompleted ? <CheckCircle /> : <Cancel />,
    };
  };

  // Get appointment date from archived appointments
  const getAppointmentDate = () => {
    const archived =
      selectedJob?.notarization?.notarization?.appointments?.archived;
    if (archived && archived.length > 0) {
      const appt = archived[0];
      const dateStr = `${appt.year}-${String(appt.month).padStart(2, "0")}-${String(appt.day_of_month).padStart(2, "0")}T${appt.time}:00`;
      return dateStr;
    }
    return selectedJob?.booked_date || null;
  };

  // Get completion date from completed_documents (use the latest created_at)
  const getCompletionDate = () => {
    const completedDocs = selectedJob?.completed_documents || [];
    if (completedDocs.length > 0) {
      // Get the latest document creation timestamp
      const latestDoc = completedDocs.reduce((latest, doc) => {
        return !latest || doc.created_at > latest.created_at ? doc : latest;
      }, null);
      if (latestDoc?.created_at) {
        // Convert Unix timestamp to ISO string
        return DateTime.fromSeconds(latestDoc.created_at).toISO();
      }
    }
    return null;
  };

  const handleViewPdf = async (pdfUrl, docIndex) => {
    const newTab = openBlankTab();
    setLoadingPdfUrls((prev) => ({ ...prev, [docIndex]: true }));
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/get-signed-document-url`,
        {
          pdfUrl: pdfUrl,
          currentSession,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success && response.data.signedUrl) {
        setNewTabLocation(newTab, response.data.signedUrl);
      } else {
        newTab?.close();
        setError("Failed to generate document URL");
      }
    } catch (error) {
      newTab?.close();
      setError(error.response?.data?.message || "Failed to open document");
    } finally {
      setLoadingPdfUrls((prev) => ({ ...prev, [docIndex]: false }));
    }
  };

  const handleDownloadDocuments = async () => {
    setDownloading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/download-documents`,
        {
          jobId: selectedJob.id,
          documentType: "all",
          currentSession,
        },
        {
          withCredentials: true,
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const filename = `notarization_${selectedJob.id}_documents.zip`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess("All documents downloaded successfully!");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to download documents");
    } finally {
      setDownloading(false);
    }
  };

  const statusInfo = getStatusInfo();

  if (!selectedJob) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: "80%", lg: "70%" },
          maxWidth: "1000px",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              p: 4,
              pb: 2,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h4" fontWeight="bold">
                Past Notarization Details
              </Typography>
              <Chip
                icon={statusInfo.icon}
                label={statusInfo.status}
                color={statusInfo.color}
                size="large"
                sx={{ px: 2, py: 1, fontSize: "1rem" }}
              />
            </Box>
          </Box>

          <Box sx={{ overflowY: "auto", flex: 1 }}>
            <CardContent sx={{ p: 4, pt: 2 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setSuccess("")}
                >
                  {success}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Completion Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                        Session Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Session Date
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDateTime(getAppointmentDate())}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Completion Date
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {formatDateTime(getCompletionDate())}
                          </Typography>
                        </Box>
                        {selectedJob?.is_failed && !isCompleted && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Failure Reason
                            </Typography>
                            <Typography variant="body2" color="error">
                              {selectedJob?.notarization?.notarization?.notary_failure?.join(
                                ", ",
                              ) || "Session failed"}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Notarization ID
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.notarization_id || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Signer Type
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.signer
                              ? selectedJob.signer.charAt(0).toUpperCase() +
                                selectedJob.signer.slice(1)
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Signer Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <Person sx={{ mr: 1, verticalAlign: "middle" }} />
                        Signer Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Full Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedJob?.signer_details
                              ? `${selectedJob.signer_details.first || ""} ${selectedJob.signer_details.middle || ""} ${selectedJob.signer_details.last || ""}`.trim() ||
                                "N/A"
                              : "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Email Address
                          </Typography>
                          <Link
                            href={`mailto:${selectedJob?.signer_details?.email || ""}`}
                            color="inherit"
                          >
                            <Typography variant="body2">
                              {selectedJob?.signer_details?.email || "N/A"}
                            </Typography>
                          </Link>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Phone Number
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.signer_details?.mobile || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Address
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.signer_details
                              ? `${selectedJob.signer_details.address || ""}, ${selectedJob.signer_details.city || ""}, ${selectedJob.signer_details.state || ""} ${selectedJob.signer_details.zip || ""}`
                                  .trim()
                                  .replace(/^,\s*|,\s*$/g, "") || "N/A"
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Project & Document Information */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        <LocationOn sx={{ mr: 1, verticalAlign: "middle" }} />
                        Project Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Property Address
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedJob?.project_address || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Property Owner
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.property_owner?.name ||
                              selectedJob?.property_owner?.personalName ||
                              "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Owner Email
                          </Typography>
                          <Link
                            href={`mailto:${selectedJob?.property_owner?.email || ""}`}
                            color="inherit"
                          >
                            <Typography variant="body2">
                              {selectedJob?.property_owner?.email || "N/A"}
                            </Typography>
                          </Link>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            County
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.county || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            City
                          </Typography>
                          <Typography variant="body2">
                            {selectedJob?.city || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Documents Section */}
                {isCompleted && (
                  <Grid size={{ xs: 12 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "400px",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant="h6" color="primary" gutterBottom>
                          <Description
                            sx={{ mr: 1, verticalAlign: "middle" }}
                          />
                          Completed Documents
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mb: 2 }}
                        >
                          {selectedJob?.completed_documents?.length > 0
                            ? `Download the notarized documents from this completed session (${selectedJob.completed_documents.length} document${selectedJob.completed_documents.length !== 1 ? "s" : ""}):`
                            : "No completed documents available."}
                        </Typography>

                        <Box sx={{ overflowY: "auto", flex: 1 }}>
                          {selectedJob?.completed_documents?.length > 0 && (
                            <List>
                              {selectedJob.completed_documents.map(
                                (doc, index) => (
                                  <ListItem key={index}>
                                    <ListItemIcon>
                                      <PictureAsPdf color="error" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        doc.name
                                          ? doc.name.charAt(0).toUpperCase() +
                                            doc.name.slice(1)
                                          : "Document"
                                      }
                                      secondary={`Signed by: ${doc.signer?.join(", ") || "N/A"} | Created: ${doc.created_at ? formatDateTime(DateTime.fromSeconds(doc.created_at).toISO()) : "N/A"}`}
                                    />
                                    {doc.pdfUrl && (
                                      <LoadingButton
                                        startIcon={<FileDownload />}
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                          handleViewPdf(doc.pdfUrl, index)
                                        }
                                        loading={loadingPdfUrls[index]}
                                      >
                                        View PDF
                                      </LoadingButton>
                                    )}
                                  </ListItem>
                                ),
                              )}
                            </List>
                          )}

                          <Box sx={{ mt: 3 }}>
                            <List>
                              <ListItem>
                                <ListItemIcon>
                                  <Download color="success" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Complete Package"
                                  secondary="All documents (signed, notarized, and original) in a single ZIP file"
                                />
                                <LoadingButton
                                  startIcon={<FileDownload />}
                                  variant="contained"
                                  size="small"
                                  onClick={handleDownloadDocuments}
                                  loading={downloading}
                                >
                                  Download All
                                </LoadingButton>
                              </ListItem>
                            </List>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Box>

          <CardActions
            sx={{
              p: 3,
              pt: 2,
              justifyContent: "flex-end",
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            {isCompleted && (
              <LoadingButton
                startIcon={<Download />}
                variant="contained"
                color="primary"
                onClick={handleDownloadDocuments}
                loading={downloading}
              >
                Download All Documents
              </LoadingButton>
            )}
          </CardActions>
        </Card>
      </Box>
    </Modal>
  );
}

export default PastNotarizationModal;
